"use client";

import Link from "next/link";
import { useEffect, useId, useMemo, useState } from "react";

type ServiceItem = {
  title: string;
  description: string;
};

export type ServicePanel = {
  number: string;
  heading: string;
  blurb: string;
  items: ServiceItem[];
  ctaHref: string;
};

const DEFAULT_PANELS: ServicePanel[] = [
  {
    number: "01",
    heading: "Concept and design",
    blurb:
      "In a world of endless feeds and tired thumbs, the brands that win are the ones that capture attention instantly and give people a reason to stop, look, and engage.",
    items: [
      {
        title: "Brand Development",
        description:
          "We develop distinctive brands through strategy, storytelling, and design, forming identities that stand apart, and drive long-term growth.",
      },
      {
        title: "Campaign Conception",
        description:
          "We transform brand foundations into campaign concepts, shaping ideas, narratives, and executions that stay consistent, but cut through noise and drive results.",
      },
      {
        title: "Creative Design",
        description:
          "We create cohesive design systems that ensure consistency across every touchpoint, reinforcing your brand story clearly and confidently from every angle.",
      },
    ],
    ctaHref: "/concept-and-design",
  },
  {
    number: "02",
    heading: "Web Design & Development",
    blurb:
      "We design websites that balance creativity with strategy, building digital experiences that capture attention, guide users intuitively, and drive action.",
 
    items: [
      {
        title: "Web Design",
        description:
          "We design high-performing websites that blend creativity and usability, guiding users effortlessly, building trust, and converting attention into conversion.",
      },
      {
        title: "Web Development",
        description:
          "We develop robust, scalable websites using Shopify, WordPress and React, ensuring performance, security, and seamless functionality that supports growth and long-term success.",
      },
      {
        title: "Beyond the Launch",
        description:
          "Hi Ho partners long-term, delivering SEO, AI search, ads, social, email, and automation to drive visibility, growth, and future-ready ecommerce performance across channels.",
      },
    ],
    ctaHref: "/website-design",
  },
  {
    number: "03",
    heading: "Digital Marketing",
    blurb:
      "In a world of endless feeds and tired thumbs, the brands that win are\r\nthe ones that capture attention instantly and give people a reason to\r\nstop, look, and engage.",
 
    items: [
      {
        title: "Social campaigns",
        description:
          "We create inbound social campaigns that connect, capture attention, and drive meaningful engagement and conversions.",
     
      },
      {
        title: "Search campaigns",
        description:
          "We strategically plan and craft SEO and pay-per-click campaigns that build awareness, capture attention, and convert high-intent audiences into customers.",
     
      },
      {
        title: "Email & SMS",
        description:
          "We create targeted email and SMS campaigns that build relationships, deliver timely messages, and convert engaged audiences into loyal customers.",
     
      },
    ],
    ctaHref: "/digital-marketing",
  },
];

export type WhatWeDoProps = {
  sectionId?: string;
  introLabel?: string;
  headingPrefix?: string;
  headingSuffix?: string;
  panels?: ServicePanel[];
  ctaLabel?: string;
  accentTextClassName?: string;
  accentButtonClassName?: string;
};

export function WhatWeDo({
  sectionId = "what-we-do",
  introLabel = "What we do",
  headingPrefix = "Creating intrigue",
  headingSuffix = "that turns them into Customers.",
  panels = DEFAULT_PANELS,
  ctaLabel = "Find out more",
  accentTextClassName = "text-[#ff5c00]",
  accentButtonClassName = "bg-[#ff5c00]",
}: WhatWeDoProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLg, setIsLg] = useState(false);
  const headingId = useId();
  const uid = useId();
  const safePanels = panels.length > 0 ? panels : DEFAULT_PANELS;
  const activeSafeIndex = Math.min(activeIndex, safePanels.length - 1);

  const tabIds = useMemo(
    () => safePanels.map((_, i) => `${uid}-tab-${i}`),
    [safePanels, uid],
  );
  const panelIds = useMemo(
    () => safePanels.map((_, i) => `${uid}-panel-${i}`),
    [safePanels, uid],
  );

  const active = safePanels[activeSafeIndex]!;

  useEffect(() => {
    const mql = window.matchMedia("(min-width: 1024px)");
    const sync = () => setIsLg(mql.matches);
    sync();
    mql.addEventListener("change", sync);
    return () => mql.removeEventListener("change", sync);
  }, []);

  return (
    <section
      id={sectionId}
      className="scroll-mt-20 bg-[#1b2631] py-14 font-sans text-white md:scroll-mt-24 md:py-20 lg:py-24"
      aria-labelledby={headingId}
    >
      <div className="site-content">
        <div className="max-w-[850px]">
          <p className="text-sm font-medium tracking-wide text-[#8fa8bf] md:text-base">
            {introLabel}
          </p>
          <h2
            id={headingId}
            className="mt-4 text-4xl leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl xl:text-7xl"
          >
            <span className={accentTextClassName}>{headingPrefix}</span>{" "}
            <span className="text-white">{headingSuffix}</span>
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-10 lg:mt-16 lg:grid-cols-12 lg:gap-12 xl:gap-16">
          <div className="order-2 hidden lg:order-1 lg:col-span-6 lg:block xl:col-span-5">
            <div
              key={activeSafeIndex}
              className="clients-panel-enter"
              role="tabpanel"
              id={panelIds[activeSafeIndex]}
              aria-labelledby={tabIds[activeSafeIndex]}
            >
              <p
                className={`text-3xl font-medium leading-none sm:text-4xl ${accentTextClassName}`}
                aria-hidden
              >
                *
              </p>
              <ul className="mt-8 space-y-8 sm:mt-10">
                {active.items.map((item) => (
                  <li key={item.title}>
                    <h3
                      className={`text-base font-semibold tracking-tight sm:text-lg md:text-3xl ${accentTextClassName}`}
                    >
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-white/90 sm:text-base md:text-xl">
                      {item.description}
                    </p>
                  </li>
                ))}
              </ul>
              <Link
                href={active.ctaHref}
                className={`mt-10 inline-flex rounded-full px-7 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#ff5c00] sm:mt-12 sm:text-base ${accentButtonClassName}`}
              >
                {ctaLabel}
              </Link>
            </div>
          </div>

          <div className="order-1 lg:order-2 lg:col-span-6 xl:col-span-7">
            <div className="flex flex-col border-t border-white/15" aria-label="Service areas">
              {safePanels.map((panel, index) => {
                const isActive = index === activeSafeIndex;
                return (
                  <div key={panel.number} className="border-b border-white/15">
                    <button
                      type="button"
                      id={tabIds[index]}
                      aria-expanded={isActive}
                      aria-controls={panelIds[index]}
                      className={`w-full cursor-pointer py-6 text-left transition-colors sm:py-8 ${
                        isActive
                          ? "text-white"
                          : "text-[#8fa8bf]/80 lg:hover:text-[#8fa8bf]"
                      }`}
                      onClick={() => setActiveIndex(index)}
                      onMouseEnter={() => {
                        if (isLg) setActiveIndex(index);
                      }}
                      onFocus={() => {
                        if (isLg) setActiveIndex(index);
                      }}
                    >
                      <span className="block text-sm font-medium tabular-nums sm:text-base">
                        {panel.number}.
                      </span>
                      <span className="mt-1 block text-xl font-semibold tracking-tight sm:text-2xl md:text-5xl">
                        {panel.heading}
                      </span>
                      <span
                        className={`mt-2 block max-w-xl text-sm leading-relaxed sm:text-base md:text-xl ${
                          isActive ? "text-white/90" : "text-[#8fa8bf]/90"
                        }`}
                      >
                        {panel.blurb}
                      </span>
                    </button>

                    <div
                      id={panelIds[index]}
                      className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-out lg:hidden ${
                        isActive ? "max-h-[900px] pb-6 opacity-100" : "max-h-0 opacity-0"
                      }`}
                      aria-hidden={!isActive}
                    >
                      <p className={`text-3xl font-medium leading-none ${accentTextClassName}`} aria-hidden>
                        *
                      </p>
                      <ul className="mt-6 space-y-6">
                        {panel.items.map((item) => (
                          <li key={item.title}>
                            <h3 className={`text-lg font-semibold tracking-tight ${accentTextClassName}`}>
                              {item.title}
                            </h3>
                            <p className="mt-2 text-sm leading-relaxed text-white/90">
                              {item.description}
                            </p>
                          </li>
                        ))}
                      </ul>
                      <Link
                        href={panel.ctaHref}
                        className={`mt-8 inline-flex rounded-full px-7 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#ff5c00] ${accentButtonClassName}`}
                      >
                        {ctaLabel}
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default WhatWeDo;
